'use client';

import Input from 'src/components/primitives/Input/Input';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { memo, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useTargetAccountData from '../../hooks/useTargetAccountData';
import { SUPER_STAKE_ACCOUNT_NAMES } from 'src/constants/constants';
import { getUserKey } from '@velocity-exchange/common';
import { accountNameIsLp } from 'src/utils/account';
import { PublicKey } from '@solana/web3.js';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import { useRenameSubaccount } from 'src/hooks/driftActions/user/useRenameSubaccount';

// TODO - This should come from SDK
const MAX_CHARS = 32;

export interface EditSubaccountModalProps {
	subaccountId: number;
}

const EditSubaccountModal = ({ subaccountId }: EditSubaccountModalProps) => {
	const set = useDriftAccountStore((s) => s.set);
	const walletAuthority = useDriftStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const { renameSubaccount } = useRenameSubaccount();
	const { hide } = useModal();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const userKey = getUserKey(subaccountId, walletAuthority as PublicKey);
	const selectedSubAccount = useTargetAccountData(userKey);

	const [newAccountName, setNewAccountName] = useState<string>(
		selectedSubAccount?.name ?? ''
	);

	if (!selectedSubAccount) return;

	const isSuperstakeAccount = SUPER_STAKE_ACCOUNT_NAMES.includes(
		selectedSubAccount.name
	);

	const isLpAccount = accountNameIsLp(selectedSubAccount.name);

	const isSubmitDisabled =
		newAccountName.length === 0 ||
		newAccountName.length > MAX_CHARS ||
		isSuperstakeAccount ||
		isLpAccount;

	const handleEditAccount = async () => {
		if (isSubmitDisabled) {
			return;
		}

		setIsSubmitting(true);

		const renameResult = await renameSubaccount(
			selectedSubAccount.userId,
			newAccountName
		);

		if (renameResult === true) {
			set((s) => {
				const user = s.accounts[userKey];
				if (user) {
					user.name = newAccountName;
				}
			});
			hide();
		}

		setIsSubmitting(false);
	};

	const charsRemaining = MAX_CHARS - newAccountName.length;

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="edit-subaccount-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="edit-subaccount-modal-close">
				Edit account name
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<Input
					id="account-name"
					labelVariant="standard"
					label="Account Name"
					type="text"
					value={newAccountName}
					onChange={setNewAccountName}
					disabled={isSuperstakeAccount}
					dataTestId="edit-account-name-input"
				/>

				{charsRemaining < 0 && (
					<InlineAlert variant="error" title="Invalid account name">
						You can&apos;t exceed the account name character limit
					</InlineAlert>
				)}

				{newAccountName.length === 0 && (
					<InlineAlert variant="error" title="Invalid account name">
						Account name cannot be empty
					</InlineAlert>
				)}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					className="flex-1"
					variant="secondary"
					onClick={hide}
					dataTestId="edit-subaccount-modal-cancel"
				>
					Cancel
				</Button>
				<Button
					size="lg"
					className="flex-1"
					disabled={isSubmitDisabled}
					isLoading={isSubmitting}
					onClick={handleEditAccount}
					dataTestId="edit-subaccount-modal-submit"
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

export default memo(EditSubaccountModal);
